import React, { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';

import { useDropdown } from '../../../tools/hooks';
import { colorStack } from '../../../styleHelpers/colors';
import { media } from '../../../styleHelpers/breakpoint';
import { SidebarMenu } from './SidebarMenu';
import { topMenuHeight } from '../../../tools/globalVariables';

const MenuWrapper = styled.div`
    background: ${colorStack.white};
    width: 90%;
    transform: translate3d(-100%, 0, 0);
    transition: transform 300ms;
    height: 100%;
    ${media.desktop`
        width: auto;
        transform: none;
    `}
`;

const SidebarNav = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    bottom: 0;
    top: ${topMenuHeight}px;
    height: calc(100% - ${topMenuHeight}px);
`;

const FullNav = styled.div<{ isMobileMenuVisible: boolean }>`
    position: fixed;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    background: ${rgba(colorStack.darkBlue, .6)};
    top: ${topMenuHeight}px;
    transition: all 300ms;
    ${({ isMobileMenuVisible }) => isMobileMenuVisible && css`
        visibility: visible;
        opacity: 1;
        transition: all 300ms;
        ${MenuWrapper} {
            transform: translate3d(0, 0, 0);
            transition: transform 300ms;
        }
    `}
    ${media.desktop`
        top: ${topMenuHeight}px;
        right: auto;
        max-width: 300px;
        transition: visibility 0s;
        transition-delay: 0s;
        background: ${colorStack.white};
    `}
`;

const Wrapper = styled.div<{ isDesktopMenuVisible: boolean }>`
    position: fixed;
    left: 0;
    top: 0;
    height: ${topMenuHeight}px;
    z-index: 106;
    width: 68px;
    padding: .5rem .5rem;
    ${FullNav} {
        display: none;
    }
    ${media.desktop`
        background: ${colorStack.white};
        bottom: 0;
        height: 100%;
        &:hover {
            ${FullNav} {
                display: block;
            }
        }
        ${({ isDesktopMenuVisible }) => isDesktopMenuVisible && css`
            ${FullNav} {
                display: block;
            }
        `}
    `}
`;

interface IProps {
    isMobileMenuVisible: boolean;
}

export const SideBar: FC<React.PropsWithChildren<IProps>> = ({ isMobileMenuVisible }) => {
    const [wrapperRef, isDesktopMenuVisible, toggleDesktopMenu, closeDesktopMenu] = useDropdown();

    const isVisible = useMemo(() => {
        return true;

        // Removes top bar on Teams app. Initially created for MS validation.
        // Disabled due to the deployment of the app to BYCN.

        // let visibility = true;

        // if (IS_TEAMS_INSTANCE) {
        //     const isClusterContext = location.pathname.split('/').includes('cluster');
        //     const isOrgContext = location.pathname.split('/').includes('orgs');
        //     if (isClusterContext || isOrgContext) {
        //         visibility = false;
        //     }
        // }
        // return visibility;
    }, []);

    return (
        <>
            {isVisible &&
                <Wrapper isDesktopMenuVisible={isDesktopMenuVisible} ref={wrapperRef} data-sidebar-wrapper>
                    <SidebarNav data-sidebar-nav>
                        <MenuWrapper>
                            <SidebarMenu
                                onMenuItemClick={closeDesktopMenu}
                                onFilterClick={toggleDesktopMenu}
                                isDesktopMenuVisible={isDesktopMenuVisible}
                            />
                        </MenuWrapper>
                    </SidebarNav>
                    <FullNav isMobileMenuVisible={isMobileMenuVisible} data-full-nav>
                        <MenuWrapper>
                            <SidebarMenu
                                fullMenu
                                onMenuItemClick={closeDesktopMenu}
                                onFilterClick={toggleDesktopMenu}
                                isDesktopMenuVisible={isDesktopMenuVisible}
                            />
                        </MenuWrapper>
                    </FullNav>
                </Wrapper>
            }
        </>
    );
};
